"use client";
import React, { useMemo, FC } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { clsx } from "clsx";

import { BreadcrumbsItem } from "~/bff/types/BreadcrumbsItem";
import { ShoppingListProduct } from "~/bff/types/ShoppingListProduct";
import { WidgetInfo } from "~/bff/types/WidgetInfo";
import { SHOPPING_LIST_LAYOUT } from "~/components/shopping-list-page/constants";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { useStoreSelectorData } from "~/hooks/store-selector/use-store-selector-data";
import { Nullable } from "~/types/general.types";

import { FlexibleProductType } from "../../component";
import { ProductGridItem } from "../product-grid-item/component";

export interface ProductsGridProps {
  className?: string;
  products: FlexibleProductType;
  breadcrumbs?: Nullable<Nullable<BreadcrumbsItem>[]>;
  layout?: string;
  productToPageMap?: Map<string, number>;
  restorationRef?: {
    markerId: Nullable<string>;
    scrollRestorationRef: React.RefObject<
      Nullable<HTMLSpanElement & HTMLAnchorElement>
    >;
  };
  pageNumberRef?: React.RefObject<number>;
  widgetData?: Nullable<WidgetInfo>;
}

export const ProductsGrid: FC<ProductsGridProps> = ({
  className,
  products,
  layout,
  breadcrumbs,
  productToPageMap,
  restorationRef,
  pageNumberRef,
  widgetData,
}) => {
  const isShoppingListLayout = layout === SHOPPING_LIST_LAYOUT;
  const storeSelectorData = useStoreSelectorData(null);

  const breadcrumbsFiltered = useMemo(
    () =>
      Array.isArray(breadcrumbs)
        ? breadcrumbs.filter<BreadcrumbsItem>(
            (el): el is BreadcrumbsItem => el !== undefined && el !== null,
          )
        : [],
    [breadcrumbs],
  );

  const productCards = useMemo(() => {
    const sortedProducts = isShoppingListLayout
      ? (products as ShoppingListProduct[])?.sort((a, b) => {
          const dateA = new Date(a?.addedAt as string);
          const dateB = new Date(b?.addedAt as string);
          return +dateB - +dateA;
        })
      : products;

    return sortedProducts?.map((productData, index) => {
      const isProductDataAbsent = !productData;
      const isProductDataIsDocs = !isProductDataAbsent && "brand" in productData;
      const isProductDataIsShoppingListProduct =
        !isProductDataAbsent && "addedAt" in productData;

      const pid = isProductDataIsDocs ? productData.pid : null;
      const productCode = isProductDataIsShoppingListProduct
        ? productData.productCode
        : null;
      const sku = isProductDataIsShoppingListProduct ? productData.sku : null;
      const masterSkuId = isProductDataIsDocs ? productData.masterSkuId : null;

      return (
        <ProductGridItem
          key={`${index}${pid ?? productCode}${sku ?? masterSkuId}`}
          productData={productData}
          isShoppingListLayout={isShoppingListLayout}
          restorationRef={restorationRef}
          layout={layout}
          index={index}
          breadcrumbs={breadcrumbsFiltered}
          pageNumberRef={pageNumberRef}
          productToPageMap={productToPageMap}
          storeSelectorData={storeSelectorData ?? undefined}
          widgetData={widgetData}
        />
      );
    });
  }, [
    isShoppingListLayout,
    products,
    restorationRef,
    layout,
    breadcrumbsFiltered,
    pageNumberRef,
    productToPageMap,
    storeSelectorData,
    widgetData,
  ]);

  return (
    <Box
      id="products-grid"
      className={clsx(className)}
      {...getTestAutomationProps("products-grid")}
    >
      <Grid container>{productCards}</Grid>
    </Box>
  );
};
